import {buildQueryFromDict, getTransformedURL, joinURL} from "./common.urls";
import {getLanguage} from './language.utils';
import APP_CONFIG from '../app.config';
import { LANGUAGE_HEADER } from './abstract.resource';


/*
 * Some pages that are loaded dynamically. You can look up page names from:
 * /wp/web/app/themes/oral.fi-theme/app/helpers.php file from $page_names variable
 */
/**
 * Path name of the reservation page in WP.
 * @type {string}
 */
const RESERVATION_PAGE_PATH = 'booking';

/**
 * Path name of the OmaOral page in WP.
 * @type {string}
 */
const OMA_ORAL_PAGE_PATH = 'oma_oral';

const BOOKING_INFO_PAGE_PATH = 'info_booking';

const LOGIN_PAGE_PATH = OMA_ORAL_PAGE_PATH;

const SECURITY_INFO_PAGE_PATH = 'security';

const TERMS_PAGE_PATH = 'terms_of_use';

const PRIVACY_PAGE_PATH = 'privacy_policy';

const PRICES_PAGE_PATH = 'prices';

const WINDOW_LOCATION_ORIGIN_REPLACER = '';

/**
 * Path of the person page in WP.
 * @type {string}
 * @return {string}
 */
const PERSON_PAGE_PATH = 'personnel';

/**
 * URL service that can be used to get links to pages that can't be linked to with a regular link. For example, if you
 * need to add query params or a hash, the WP /op/ redirect will not preserve them. So instead we can fetch the correct
 * URL with UrlService and use that directly.
 */
class UrlService {
  constructor($http, $q, $sessionStorage, AbstractResource) {
    'ngInject';

    this.$http = $http;
    this.$sessionStorage = $sessionStorage;
    this.$q = $q;
    this.AbstractResource = AbstractResource;

    this.url = `${APP_CONFIG.be}/urls/`;

    /**
     * An object that can be used as cache for URLS to different pages.
     * Keys are page names, values are promises.
     *
     * @type {Object.<string, string> | null}
     */
    this.RECEIVED_URL_TRANSFORMS = null;
    this.initializePromise = this.initialize();
  }

  /**
   * Initialize URLs for the current language.
   * @return {Promise}
   */
  initialize() {
    const language = getLanguage();

    // Language hasn't changed, don't fetch urls again
    if (!this.shouldUpdateUrls(language)) {
      if (this.RECEIVED_URL_TRANSFORMS == null) {
        this.RECEIVED_URL_TRANSFORMS = this.$sessionStorage.urls;
      }

      return this.$q.when(this.RECEIVED_URL_TRANSFORMS);
    }

    // Language has changed, prefetch all urls
    return this.updateUrls(language);
  }

  /**
   * Check if urls should be fetched.
   * Should be fetched if urls are not found from sessions storage.
   * Also should be fetched if language in session storage is not se or language has changed.
   * @param {string} language
   * @return {boolean}
   */
  shouldUpdateUrls(language) {
    if (this.$sessionStorage.urls == null) {
      return true;
    }
    else if (this.$sessionStorage.urlLang !== language) {
      return true;
    }

    return false;
  }

  /**
   * Update URLs for the given language.
   * @param {string} language
   * @return {Promise.<void>}
   */
  updateUrls(language) {
    const headers = {[LANGUAGE_HEADER]: language}
    return this.$http.get(this.AbstractResource.resolveUrl(this.url), {headers: headers})
    .then(response => {
      if (response.status === 200) {
        if (!Array.isArray(response.data)) {
          return
        }
        this.RECEIVED_URL_TRANSFORMS = {};
        for (const obj of response.data) {
            if (typeof obj === 'object' && obj != null &&
              'url' in obj && 'key' in obj &&
              typeof obj.url === 'string' && typeof obj.key === 'string') {
              this.RECEIVED_URL_TRANSFORMS[obj.key] = obj.url;
            }
        }
        this.$sessionStorage.urls = this.RECEIVED_URL_TRANSFORMS;
        this.$sessionStorage.urlLang = language;
      }
    });
  }

  /**
   * Get URL for the given page.
   * @param {string} url
   * @return {Promise.<string>}
   */
  getUrl(url) {
    return this.initializePromise
      .then(() => {
        if (url in this.RECEIVED_URL_TRANSFORMS) {
          return this.$q.when(this.RECEIVED_URL_TRANSFORMS[url]);
        }

        // Add /url to get the URL to redirect to as JSON
        const req_url = joinURL(url, 'url/');

        return this.$http.get(getTransformedURL(req_url))
          .then(response => {
            this.RECEIVED_URL_TRANSFORMS[url] = response.data.url;
            return this.RECEIVED_URL_TRANSFORMS[url];
          });
      })
  }

  /**
   * Get URL to reservation page.
   *
   * Given parameter can have keys 'personId', 'locationId', 'serviceId' or 'editId'
   * (or a combination), with the values being IDs.
   *
   * @return {Promise.<string>}
   */
  reservationURL({personId, locationId, cityId, serviceId, timeslotId, editId, voucher, temporaryId}={}) {
    let params = {};

    if (serviceId != null) {
      params.serviceId = serviceId;
    }
    if (timeslotId != null) {
      params.timeslotId = timeslotId;
    }

    // Person is preferred over location
    if (personId != null) {
      params.personId = personId;
    }
    else if (locationId != null) {
      params.locationId = locationId;
    }
    else if (cityId != null) {
      params.cityId = cityId;
    }
    else if (editId != null) {
      params.edit = editId;
    }

    if (voucher != null) {
      params.vo = voucher;
    }

    if (editId == null && temporaryId != null) {
      params.temporary = temporaryId;
    }

    return this.getUrl(RESERVATION_PAGE_PATH)
    .then(url => `${url}#/?${buildQueryFromDict(params)}`);
  }

  /**
   * Get URL to OmaOral page.
   * @param {string} path Optional path to go to in OmaOral instead of the default index page. Must include beginning
   *   forward slash.
   * @return {Promise.<string>}
   */
  omaOralURL(path='/') {
    return this.getUrl(OMA_ORAL_PAGE_PATH).then(url => `${url}#${path}`);
  }

  bookingInfoURL() {
    return this.getUrl(BOOKING_INFO_PAGE_PATH)
  }

  loginURL() {
    return this.getUrl(LOGIN_PAGE_PATH);
  }

  securityURL() {
    return this.getUrl(SECURITY_INFO_PAGE_PATH);
  }

  termsURL() {
    return this.getUrl(TERMS_PAGE_PATH);
  }

  privacyURL() {
    return this.getUrl(PRIVACY_PAGE_PATH);
  }

  pricesUrl() {
    return this.getUrl(PRICES_PAGE_PATH);
  }

  personListURL() {
    return this.getUrl(PERSON_PAGE_PATH);
  }

  /**
   * Transform full path to relative path
   * @param {string} url
   */
  getRelativePath(url) {
    return url.replace(window.location.origin, WINDOW_LOCATION_ORIGIN_REPLACER);
  }
}

const urlMod = angular.module('app.common.urls', [])

/**
 * UrlService can be used to fetch certain WP URLs for example to reservation page.
 */
.service('UrlService', UrlService);

export {
  RESERVATION_PAGE_PATH,
};
export default urlMod.name;
